import i18next from 'i18next';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { createSegment } from 'common/api/v1/segmentsLayout';
import {
    solarCharacterization as defaultSolarCharacterization,
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
} from 'common/components/maps/constants';
import { POLYGON_SEGMENT } from 'common/components/segment/constants';
import { formatSegment } from 'common/components/segment/helpers';
import { showReponseErrorsAsAlert } from 'common/utils/helpers';
import {
    getFrameSpacingViaSystemUid,
    getMinCol,
    getMinMaxGroupTilt,
    getMinRow,
    getOrientationViaSystemUid,
} from 'common/utils/helpers/systemsValues';
import showToast from 'common/utils/showToast';

import { detectFieldChanges } from '../../multiPanelLayout/helpers';
import * as multiPanelLayoutSelectors from '../../multiPanelLayout/selectors';
import {
    SAVE_SEGMENT,
    SAVE_SEGMENT_FAILURE,
    SAVE_SEGMENT_SUCCESS,
} from '../actionTypes';
import { actions } from '../reducer';
import * as selectors from '../selectors';

import prepareEditSegment from './prepareEditSegment';
import selectSegment from './selectSegment';
import updateSegments from './updateSegments';

const buildSegmentPayload = ({
    config,
    parentFieldSegment,
    presetConfiguration,
    segments,
}) => {
    const { proposal, id: commercialOfferId } = parentFieldSegment;
    const { panel, system } = proposal;
    const systemId = !isEmpty(system) ? system.id : config?.system;

    let defaultOfferPanelValues = {
        assigned: 0,
        color: SEGMENT_COLOR,
        id: null,
        quantity: 0,
        stroke_color: SEGMENT_STROKE_COLOR,
    };
    let buildingHeading = null;
    let module = null;

    if (panel) {
        module = panel;
        defaultOfferPanelValues = {
            ...defaultOfferPanelValues,
            ...module,
        };
    }

    const { assigned, color, id, stroke_color } = defaultOfferPanelValues;

    const { building_height: height = 0, category, system_uid } = config;

    let azimuth = defaultSolarCharacterization.azimuth;

    const ceil_tilt = config.ceil_tilt || 0;
    const orientation = getOrientationViaSystemUid(system_uid);
    const minCols = getMinCol(system_uid);
    const minRows = getMinRow(system_uid);
    const [tilt] = getMinMaxGroupTilt(system_uid);
    const { frame_row_spacing, frame_col_spacing } =
        getFrameSpacingViaSystemUid(system_uid);

    const solarCharacterization = {
        ...defaultSolarCharacterization,
        assigned,
        height: height || 0,
        polygon: [],
        quantity: 0,
        azimuth,
        ceil_tilt,
        frame_col_spacing,
        frame_cols_number: minCols,
        frame_row_spacing,
        frame_rows_number: minRows,
        orientation,
        tilt,
    };

    const countSegments = segments.filter(
        (segment) => segment.type === POLYGON_SEGMENT,
    ).length;

    const field_segments = [
        {
            name: `${i18next.t('Segment')} ${countSegments + 1}`,
            polygon: [],
            solar_modules: [],
        },
    ];

    const solution_fields = {
        beam_spacing: config.beam_spacing,
        front_base_height: config.front_base_height,
        lfoot_supports_width: config.lfoot_supports_width,
        outer_rail_length: config.outer_rail_length,
        plate_support_length: config.plate_support_length,
        rail_spacing: config.rail_spacing,
        rail_support_length: config.rail_support_length,
        seam_distance: config.seam_distance,
        sheet_gauge: config.sheet_gauge,
        support_length: config.support_length,
    };

    const { hasChanges = false } = detectFieldChanges(
        presetConfiguration,
        solution_fields,
    );

    const additionalCustomInputs = () => {
        if (config?.preset_configuration && !hasChanges)
            return { preset_configuration: config.preset_configuration };
        return { ...solution_fields };
    };

    const payload = {
        asce_metadata: [],
        category: category || null,
        commercial_offer: commercialOfferId,
        custom_inputs: {
            algorithm: 'dynamic',
            building_heading: buildingHeading,
            building_height: config.building_height,
            category,
            ceil_tilt: config.ceil_tilt,
            fill_algorithm: config.fill_algorithm,
            floor_height: config.floor_height,
            gclamp_type: config.gclamp_type,
            panel_orientation: orientation,
            sloof_type: config.sloof_type,
            system_uid: config.system_uid,
            tilt,
            type_application: config.type_application,
            ...additionalCustomInputs(),
        },
        field_segments,
        groups: [],
        parent_field_segment: parentFieldSegment.id,
        segment_color: color,
        segment_stroke_color: stroke_color,
        solar_module: id,
        system: systemId,
        system_uid: system_uid || null,
        total_area: 0,
        type: POLYGON_SEGMENT,
        zIndex: 0,
        ...solarCharacterization,
    };

    return payload;
};

export default (config) => (dispatch, getState) => {
    const state = getState();
    const parentFieldSegment = selectors.getParentFieldSegmentData(state);
    const segments = selectors.getSegmentsData(state);
    const presetConfiguration =
        multiPanelLayoutSelectors.getPresetConfiguration(state);
    const payload = buildSegmentPayload({
        config,
        parentFieldSegment,
        presetConfiguration,
        segments,
    });

    dispatch(actions[SAVE_SEGMENT]());

    createSegment(payload)
        .then((response) => {
            const segmentData = get(response, 'data', {});
            const formatedSegment = formatSegment(
                segmentData,
                parentFieldSegment.id,
            );
            const newSegment = formatedSegment;
            dispatch(actions[SAVE_SEGMENT_SUCCESS](formatedSegment));

            const newValuesSegments = [...segments, newSegment];
            const updatedSegments = newValuesSegments.map((segment) => ({
                ...segment,
                open: newSegment.id === segment.id,
            }));

            dispatch(updateSegments(updatedSegments));
            dispatch(selectSegment(newSegment));
            dispatch(prepareEditSegment(newSegment.id));

            showToast({ body: i18next.t('The segment has been saved') });
        })
        .catch((error) => {
            dispatch(
                actions[SAVE_SEGMENT_FAILURE](error?.response?.data?.errors),
            );
            showReponseErrorsAsAlert(dispatch, error.response);
        });
};
